import type { SupabaseClient } from "@supabase/supabase-js"
import * as DocumentPicker from "expo-document-picker"

export const EQ_PROOF_EVIDENCE_BUCKET = "eq-proof-evidence"
const MAX_EVIDENCE_FILE_BYTES = 50 * 1024 * 1024

const allowedMimeTypes = new Set([
  "video/mp4",
  "video/quicktime",
  "video/webm",
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
  "text/plain",
])

const allowedPickerTypes = Array.from(allowedMimeTypes)

export type EqEvidenceFile = {
  uri: string
  name: string
  mimeType: string
  size?: number | null
}

export type EqEvidenceRef = {
  kind: "upload"
  label: string
  bucket: string
  path: string
  fileName: string
  mimeType: string
  fileSize: number
}

function sanitizeFileName(fileName: string) {
  const cleaned = fileName
    .trim()
    .replace(/[/\\?%*:|"<>]/g, "-")
    .replace(/\s+/g, "-")
    .slice(0, 120)

  return cleaned || "proof-evidence"
}

export function validateEvidenceFile(file: EqEvidenceFile) {
  if (!file.uri) return "Choose a file before logging proof."
  if (!allowedMimeTypes.has(file.mimeType)) {
    return "Use a supported evidence file: video, image, PDF, or text."
  }
  if ((file.size ?? 0) > MAX_EVIDENCE_FILE_BYTES) {
    return "Evidence files must be 50 MB or smaller."
  }

  return null
}

function inferMimeType(name: string) {
  const extension = name.split(".").pop()?.toLowerCase()
  if (extension === "mp4") return "video/mp4"
  if (extension === "mov") return "video/quicktime"
  if (extension === "webm") return "video/webm"
  if (extension === "jpg" || extension === "jpeg") return "image/jpeg"
  if (extension === "png") return "image/png"
  if (extension === "webp") return "image/webp"
  if (extension === "pdf") return "application/pdf"
  if (extension === "txt") return "text/plain"

  return "application/octet-stream"
}

export async function pickProofEvidenceFile(): Promise<EqEvidenceFile | null> {
  const result = await DocumentPicker.getDocumentAsync({
    type: allowedPickerTypes,
    multiple: false,
    copyToCacheDirectory: true,
  })

  if (result.canceled || result.assets.length === 0) {
    return null
  }

  const asset = result.assets[0]
  return {
    uri: asset.uri,
    name: asset.name,
    mimeType: asset.mimeType ?? inferMimeType(asset.name),
    size: asset.size,
  }
}

async function fileToBlob(file: EqEvidenceFile) {
  const response = await fetch(file.uri)
  return response.blob()
}

export async function uploadProofEvidence({
  client,
  userId,
  proofId,
  file,
}: {
  client: SupabaseClient
  userId: string
  proofId: string
  file: EqEvidenceFile
}): Promise<EqEvidenceRef> {
  const validationError = validateEvidenceFile(file)
  if (validationError) throw new Error(validationError)

  const safeName = sanitizeFileName(file.name)
  const storagePath = `${userId}/${proofId}/${Date.now()}-${safeName}`
  const blob = await fileToBlob(file)
  const fileSize = file.size ?? blob.size ?? 0

  const { error: uploadError } = await client.storage
    .from(EQ_PROOF_EVIDENCE_BUCKET)
    .upload(storagePath, blob, {
      contentType: file.mimeType,
      upsert: false,
    })

  if (uploadError) throw uploadError

  const { error: insertError } = await client.from("eq_proof_evidence_files").insert({
    proof_id: proofId,
    user_id: userId,
    bucket_id: EQ_PROOF_EVIDENCE_BUCKET,
    storage_path: storagePath,
    file_name: file.name,
    mime_type: file.mimeType,
    file_size: fileSize,
  })

  if (insertError) {
    await client.storage.from(EQ_PROOF_EVIDENCE_BUCKET).remove([storagePath])
    throw insertError
  }

  return {
    kind: "upload",
    label: file.name,
    bucket: EQ_PROOF_EVIDENCE_BUCKET,
    path: storagePath,
    fileName: file.name,
    mimeType: file.mimeType,
    fileSize,
  }
}
